import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Sparkles,
  Wand2,
  ExternalLink,
  Loader2,
  Send,
  Image,
  Cpu,
  ChevronDown,
  Trash2,
  Copy,
  Check,
  CheckCircle2,
  Circle,
  Download,
  Layers,
  Palette,
  Lightbulb,
  Sun,
  Moon,
  Save,
} from "lucide-react";
import { apiClient } from "../lib/api";
import type { DesignVariation, DesignCategory, AIModelInfo } from "@erc/shared";

const CATEGORY_OPTIONS: { id: DesignCategory; label: string; description: string; icon: typeof Layers }[] = [
  {
    id: "same",
    label: "Tasarımın Aynısı",
    description: "Orijinal tasarımı birebir yeniden oluşturur",
    icon: Layers,
  },
  {
    id: "similar",
    label: "Tasarımın Benzeri",
    description: "Benzer ama farklı detaylarla varyasyonlar",
    icon: Palette,
  },
  {
    id: "themed",
    label: "Aynı Temalı Farklı Tasarımlar",
    description: "Aynı temadan ilham alan tamamen yeni tasarımlar",
    icon: Lightbulb,
  },
  {
    id: "light",
    label: "Açık Zemin Versiyonu",
    description: "Beyaz/açık renkli tişörtler için koyu tonlu tasarım",
    icon: Sun,
  },
  {
    id: "dark",
    label: "Koyu Zemin Versiyonu",
    description: "Siyah/koyu renkli tişörtler için açık tonlu tasarım",
    icon: Moon,
  },
];

const CATEGORY_LABELS: Record<string, string> = {
  same: "Tasarımın Aynısı",
  similar: "Tasarımın Benzeri",
  themed: "Aynı Temalı Farklı Tasarımlar",
  light: "Açık Zemin Versiyonu",
  dark: "Koyu Zemin Versiyonu",
};

export default function ListingBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedModel, setSelectedModel] = useState("");
  const [selectedImageModels, setSelectedImageModels] = useState<string[]>([]);
  const [showModelSelect, setShowModelSelect] = useState(false);
  const [showImageModelSelect, setShowImageModelSelect] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedCategories, setSelectedCategories] = useState<Set<DesignCategory>>(new Set());
  const [variationsPerCategory, setVariationsPerCategory] = useState(1);
  const [customPrompt, setCustomPrompt] = useState("");
  const [showCustom, setShowCustom] = useState(false);

  const { data: project, isLoading } = useQuery({
    queryKey: ["builder-project", id],
    queryFn: () => apiClient.getBuilderProject(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.designVariations?.some((v) => v.status === "generating")) return 2000;
      return false;
    },
  });

  const { data: models } = useQuery({
    queryKey: ["builder-models"],
    queryFn: () => apiClient.getBuilderModels(),
  });

  const analyzeMutation = useMutation({
    mutationFn: () => apiClient.analyzeProduct(id!, selectedModel || undefined),
  });

  const batchMutation = useMutation({
    mutationFn: (payload: { categories: string[]; models?: string[]; analysisContext?: string; variationsPerCategory?: number }) =>
      apiClient.createBatchVariations(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["builder-project", id] });
      setSelectedCategories(new Set());
    },
  });

  const variationMutation = useMutation({
    mutationFn: (vars: { prompt: string; model: string }) =>
      apiClient.createVariation(id!, vars),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["builder-project", id] });
      setCustomPrompt("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.deleteBuilderProject(id!),
    onSuccess: () => navigate("/design-builder"),
  });

  if (isLoading) {
    return (
      <div className="mx-auto max-w-6xl p-8">
        <div className="h-8 w-48 animate-pulse rounded bg-gray-200 mb-6" />
        <div className="h-96 animate-pulse rounded-xl bg-gray-100" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center p-20 text-center">
        <Image className="mb-3 h-12 w-12 text-gray-300" />
        <p className="text-gray-500">Proje bulunamadı</p>
        <Link to="/design-builder" className="mt-3 text-sm text-indigo-600 hover:text-indigo-700">
          Geri dön
        </Link>
      </div>
    );
  }

  const visionModels = models?.vision ?? [];
  const imageGenModels = models?.imageGen ?? [];
  const currentAnalysisModel = visionModels.find((m) => m.id === selectedModel) ?? visionModels[0];
  // Secili modeller: yoksa ilk modeli varsayilan olarak kullan
  const activeImageModels = selectedImageModels.length > 0
    ? selectedImageModels
    : imageGenModels.length > 0 ? [imageGenModels[0].id] : [];

  const hasVariations = (project.designVariations?.length ?? 0) > 0;
  const analysisComplete = !!analyzeMutation.data || hasVariations;

  function toggleCategory(cat: DesignCategory) {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  function toggleImageModel(modelId: string) {
    setSelectedImageModels((prev) => {
      if (prev.includes(modelId)) return prev.filter((m) => m !== modelId);
      return [...prev, modelId];
    });
  }

  function handleBatchGenerate() {
    if (selectedCategories.size === 0) return;
    batchMutation.mutate({
      categories: Array.from(selectedCategories),
      models: activeImageModels,
      analysisContext: analyzeMutation.data?.analysis,
      variationsPerCategory,
    });
  }

  function handleCustomSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customPrompt.trim()) return;
    variationMutation.mutate({
      prompt: customPrompt.trim(),
      model: activeImageModels[0] || "",
    });
  }

  function handleCopyResult(variationId: string, text: string) {
    navigator.clipboard.writeText(text);
    setCopiedId(variationId);
    setTimeout(() => setCopiedId(null), 2000);
  }

  async function handleDeleteVariation(variationId: string) {
    try {
      await apiClient.deleteVariation(id!, variationId);
      queryClient.invalidateQueries({ queryKey: ["builder-project", id] });
    } catch {
      // silently fail
    }
  }

  async function handleSaveToDesigns(imageUrl: string, name: string) {
    try {
      await apiClient.saveDesignFromUrl(imageUrl, {
        source: "design-builder",
        sourceId: id,
        name: `${project?.title || "Design"} - ${name}`,
      });
      alert("Tasarım havuzuna eklendi!");
    } catch (err) {
      alert("Tasarım eklenemedi: " + (err as Error).message);
    }
  }

  function handleDownload(imageUrl: string, index: number) {
    const fullUrl = imageUrl.startsWith("http") ? imageUrl : imageUrl;
    const a = document.createElement("a");
    a.href = fullUrl;
    a.download = `design-${index + 1}.png`;
    a.click();
  }

  const bigImageUrl = project.sourceImageUrl?.replace(/il_\d+x\d+/, "il_1588xN") ?? "";

  // Varyasyonlari kategoriye gore grupla
  const grouped = (project.designVariations ?? []).reduce<Record<string, DesignVariation[]>>((acc, v) => {
    const cat = v.category ?? "custom";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(v);
    return acc;
  }, {});

  const categoryOrder = ["same", "similar", "themed", "light", "dark", "custom"];
  const sortedGroups = categoryOrder.filter((c) => grouped[c]?.length).map((c) => ({
    category: c,
    label: CATEGORY_LABELS[c] ?? "Özel Talimat",
    variations: grouped[c],
  }));

  return (
    <div className="mx-auto max-w-5xl p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <Link
          to="/design-builder"
          className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Projeler
        </Link>
        <button
          onClick={() => { if (confirm("Bu projeyi silmek istediğinize emin misiniz?")) deleteMutation.mutate(); }}
          className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Sil
        </button>
      </div>

      {/* Kaynak Gorsel - Kompakt */}
      <div className="mb-6 flex items-start gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {project.sourceImageUrl ? (
          <img
            src={bigImageUrl}
            alt=""
            className="h-32 w-32 rounded-lg object-contain bg-gray-50 border border-gray-100 flex-shrink-0"
          />
        ) : (
          <div className="flex h-32 w-32 items-center justify-center rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0">
            <Image className="h-10 w-10 text-gray-300" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold text-gray-900 line-clamp-2">{project.title}</h1>
          {project.sourceListingUrl && (
            <a
              href={project.sourceListingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
            >
              <ExternalLink className="h-3 w-3" />
              Etsy'de Gör
            </a>
          )}
        </div>
      </div>

      {/* ===== ADIM 1: ANALIZ ===== */}
      <div className="mb-6">
        <StepHeader
          step={1}
          title="Tasarımı Analiz Et"
          description="AI modeli seçip görseli analiz ettirin. Tasarım stili, renkler ve hedef kitle hakkında bilgi alın."
          completed={analysisComplete}
          active={!analysisComplete}
        />
        <div className="ml-10 mt-3">
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-end gap-3">
              {/* Model secimi */}
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Vision Model</label>
                <div className="relative">
                  <button
                    onClick={() => setShowModelSelect(!showModelSelect)}
                    className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <span className="flex items-center gap-2">
                      <Cpu className="h-4 w-4 text-gray-400" />
                      {currentAnalysisModel?.name ?? "Model seç..."}
                    </span>
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </button>
                  {showModelSelect && (
                    <div className="absolute top-full mt-1 left-0 right-0 z-50 max-h-72 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {visionModels.map((m: AIModelInfo) => (
                        <button
                          key={m.id}
                          onClick={() => { setSelectedModel(m.id); setShowModelSelect(false); }}
                          className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                            m.id === selectedModel ? "bg-indigo-50 text-indigo-700" : "text-gray-700"
                          }`}
                        >
                          <span className="font-medium">{m.name}</span>
                          <span className="ml-2 text-xs text-gray-400">{m.description}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Analiz butonu */}
              <button
                onClick={() => analyzeMutation.mutate()}
                disabled={analyzeMutation.isPending}
                className="flex items-center gap-2 rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-50 transition-colors flex-shrink-0"
              >
                {analyzeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                {analyzeMutation.isPending ? "Analiz ediliyor..." : "Analiz Et"}
              </button>
            </div>

            {/* Analiz sonucu */}
            {analyzeMutation.data && (
              <div className="mt-4 rounded-lg border border-purple-200 bg-purple-50 p-4">
                <p className="text-sm text-purple-900 whitespace-pre-wrap leading-relaxed">{analyzeMutation.data.analysis}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== ADIM 2: TASARIM KATEGORILERI ===== */}
      <div className={`mb-6 transition-opacity duration-300 ${analysisComplete ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
        <StepHeader
          step={2}
          title="Tasarım Üret"
          description="Kategori ve model seçin. Birden fazla model seçerek farklı AI'ların çıktılarını karşılaştırın."
          completed={hasVariations}
          active={analysisComplete && !hasVariations}
        />
        <div className="ml-10 mt-3 space-y-3">
          {/* Kategori secimi */}
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            {/* Model secimi - coklu */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-500">Görsel Üretim Modelleri</label>
                <span className="text-[10px] text-gray-400">
                  {activeImageModels.length} model seçili — farklı modeller karşılaştırmak için
                </span>
              </div>
              <button
                onClick={() => setShowImageModelSelect(!showImageModelSelect)}
                className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <span className="flex items-center gap-2 flex-wrap">
                  <Cpu className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  {activeImageModels.length === 0 ? (
                    <span className="text-gray-400">Model seç...</span>
                  ) : activeImageModels.length <= 3 ? (
                    activeImageModels.map((mid) => {
                      const m = imageGenModels.find((x) => x.id === mid);
                      return (
                        <span key={mid} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-xs text-indigo-700">
                          {m?.name ?? mid.split("/").pop()}
                        </span>
                      );
                    })
                  ) : (
                    <span>{activeImageModels.length} model seçili</span>
                  )}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${showImageModelSelect ? "rotate-180" : ""}`} />
              </button>
              {showImageModelSelect && (
                <div className="mt-1 rounded-lg border border-gray-200 bg-white shadow-lg max-h-72 overflow-y-auto">
                  {imageGenModels.map((m: AIModelInfo) => {
                    const isSelected = activeImageModels.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        onClick={() => toggleImageModel(m.id)}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                          isSelected ? "bg-indigo-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          readOnly
                          className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600"
                        />
                        <span className={`font-medium ${isSelected ? "text-indigo-700" : "text-gray-700"}`}>{m.name}</span>
                        <span className="text-xs text-gray-400">{m.description}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="space-y-2">
              {CATEGORY_OPTIONS.map(({ id: catId, label, description, icon: Icon }) => {
                const checked = selectedCategories.has(catId);
                return (
                  <label
                    key={catId}
                    className={`flex items-center gap-3 rounded-lg border-2 p-3 cursor-pointer transition-all ${
                      checked
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCategory(catId)}
                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <Icon className={`h-5 w-5 flex-shrink-0 ${checked ? "text-indigo-600" : "text-gray-400"}`} />
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium ${checked ? "text-indigo-900" : "text-gray-700"}`}>{label}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{description}</p>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Kategori basi varyasyon sayisi */}
            <div className="mt-3 flex items-center gap-3">
              <label className="text-sm text-gray-600 whitespace-nowrap">Kategori başı:</label>
              <div className="flex items-center gap-1">
                {[1, 2, 3].map((n) => (
                  <button
                    key={n}
                    onClick={() => setVariationsPerCategory(n)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      variationsPerCategory === n
                        ? "bg-indigo-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <span className="text-xs text-gray-400">varyasyon</span>
            </div>

            {/* Uret butonu */}
            <button
              onClick={handleBatchGenerate}
              disabled={selectedCategories.size === 0 || batchMutation.isPending}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {batchMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              {batchMutation.isPending
                ? "Tasarımlar oluşturuluyor..."
                : `${selectedCategories.size * activeImageModels.length * variationsPerCategory} Tasarım Oluştur`}
            </button>
          </div>

          {/* Ozel talimat (acilir kapanir) */}
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <button
              onClick={() => setShowCustom(!showCustom)}
              className="flex w-full items-center justify-between px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-50"
            >
              <span className="flex items-center gap-2">
                <Wand2 className="h-3.5 w-3.5" />
                Özel Talimat
              </span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showCustom ? "rotate-180" : ""}`} />
            </button>
            {showCustom && (
              <div className="px-4 pb-4">
                <form onSubmit={handleCustomSubmit} className="flex gap-3">
                  <input
                    type="text"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="Örneğin: Aynı figürleri pastel renklerde, t-shirt için..."
                    className="flex-1 rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                  />
                  <button
                    type="submit"
                    disabled={!customPrompt.trim() || variationMutation.isPending}
                    className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                  >
                    {variationMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Oluştur
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ===== ADIM 3: SONUCLAR ===== */}
      <div className={`transition-opacity duration-300 ${hasVariations ? "opacity-100" : "opacity-40"}`}>
        <StepHeader
          step={3}
          title="Sonuçlar"
          description="Oluşturulan tasarımları inceleyin, indirin veya Mockup Studio'ya gönderin."
          completed={false}
          active={hasVariations}
        />
        <div className="ml-10 mt-3">
          {sortedGroups.length > 0 ? (
            <div className="space-y-6">
              {sortedGroups.map(({ category, label, variations }) => (
                <div key={category}>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                    {category === "same" && <Layers className="h-3.5 w-3.5" />}
                    {category === "similar" && <Palette className="h-3.5 w-3.5" />}
                    {category === "themed" && <Lightbulb className="h-3.5 w-3.5" />}
                    {category === "light" && <Sun className="h-3.5 w-3.5" />}
                    {category === "dark" && <Moon className="h-3.5 w-3.5" />}
                    {category === "custom" && <Wand2 className="h-3.5 w-3.5" />}
                    {label}
                    <span className="text-gray-400 font-normal normal-case">({variations.length} varyasyon)</span>
                  </h3>
                  <div className="space-y-4">
                    {variations.map((v, idx) => (
                      <VariationCard
                        key={v.id}
                        variation={v}
                        sourceImage={bigImageUrl}
                        copiedId={copiedId}
                        index={idx}
                        onCopy={handleCopyResult}
                        onDownload={handleDownload}
                        onDelete={handleDeleteVariation}
                        onSaveToDesigns={handleSaveToDesigns}
                        draftId={id}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-12">
              <Wand2 className="mb-2 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-400">Henüz tasarım oluşturulmadı</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Adim basligi */
function StepHeader({
  step,
  title,
  description,
  completed,
  active,
}: {
  step: number;
  title: string;
  description: string;
  completed: boolean;
  active: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex-shrink-0 mt-0.5">
        {completed ? (
          <CheckCircle2 className="h-6 w-6 text-emerald-500" />
        ) : active ? (
          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-600 text-white text-xs font-bold">
            {step}
          </div>
        ) : (
          <Circle className="h-6 w-6 text-gray-300" />
        )}
      </div>
      <div>
        <h2 className={`text-sm font-semibold ${active ? "text-gray-900" : completed ? "text-emerald-700" : "text-gray-400"}`}>
          {title}
        </h2>
        <p className="text-xs text-gray-400 mt-0.5">{description}</p>
      </div>
    </div>
  );
}

/* Varyasyon karti */
function VariationCard({
  variation,
  sourceImage,
  copiedId,
  index,
  onCopy,
  onDownload,
  onDelete,
  onSaveToDesigns,
  draftId,
}: {
  variation: DesignVariation;
  sourceImage: string;
  copiedId: string | null;
  index: number;
  onCopy: (id: string, text: string) => void;
  onDownload: (imageUrl: string, index: number) => void;
  onDelete: (id: string) => void;
  onSaveToDesigns: (imageUrl: string, name: string) => void;
  draftId?: string;
}) {
  const statusConfig = {
    pending: { color: "bg-gray-100 text-gray-600", label: "Bekliyor" },
    generating: { color: "bg-yellow-100 text-yellow-700", label: "Oluşturuluyor..." },
    completed: { color: "bg-emerald-100 text-emerald-700", label: "Tamamlandı" },
    failed: { color: "bg-red-100 text-red-700", label: "Başarısız" },
  };
  const status = statusConfig[variation.status];

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <span className={`inline-block rounded-full px-2.5 py-0.5 text-[10px] font-medium ${status.color}`}>
            {status.label}
          </span>
          <span className="text-xs text-gray-400">{variation.model.split("/").pop()}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">
            {new Date(variation.createdAt).toLocaleString("tr-TR")}
          </span>
          <button
            onClick={() => onDelete(variation.id)}
            className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
            title="Sil"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Prompt */}
      <div className="px-4 py-2 bg-indigo-50/50 border-b border-indigo-100">
        <p className="text-xs text-indigo-800 line-clamp-2">
          <span className="font-semibold">Talimat:</span> {variation.prompt}
        </p>
      </div>

      {/* Icerik */}
      <div className="p-4">
        {variation.status === "generating" && (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-gray-500">
            <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
            AI tasarım oluşturuyor...
          </div>
        )}

        {variation.status === "completed" && (variation.result || variation.imageUrl) && (
          <div>
            {variation.imageUrl ? (
              /* Gorsel uretim sonucu */
              <div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">Orijinal Tasarım</p>
                    <img src={sourceImage} alt="" className="w-full rounded-lg object-contain bg-gray-50 border border-gray-100" />
                  </div>
                  <div>
                    <p className="text-[10px] font-medium text-emerald-500 uppercase tracking-wider mb-1.5">AI Üretilen Tasarım</p>
                    <img
                      src={variation.imageUrl}
                      alt="AI generated design"
                      className="w-full rounded-lg object-contain border-2 border-emerald-200"
                      style={{ background: "repeating-conic-gradient(#f3f4f6 0% 25%, #fff 0% 50%) 50% / 16px 16px" }}
                    />
                    {/* DPI/Format badge */}
                    <div className="mt-2 flex items-center gap-1.5">
                      <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[9px] font-medium text-gray-600">300 DPI</span>
                      <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[9px] font-medium text-gray-600">PNG</span>
                      <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[9px] font-medium text-gray-600">Şeffaf Arka Plan</span>
                    </div>
                  </div>
                </div>
                {variation.result && variation.result !== "Görsel başarıyla oluşturuldu." && (
                  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 text-sm text-gray-600 whitespace-pre-wrap mb-3">
                    {variation.result}
                  </div>
                )}
              </div>
            ) : (
              /* Metin tabanli sonuc */
              <div>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">Orijinal Tasarım</p>
                    <img src={sourceImage} alt="" className="w-full rounded-lg object-contain bg-gray-50 border border-gray-100" />
                  </div>
                  <div>
                    <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">AI Tasarım Önerisi</p>
                    <div className="w-full rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100 p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap overflow-y-auto" style={{ maxHeight: "400px" }}>
                      {variation.result}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              {variation.imageUrl && (
                <>
                  <button
                    onClick={() => onDownload(variation.imageUrl!, index)}
                    className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors border border-indigo-200"
                  >
                    <Download className="h-3.5 w-3.5" />
                    İndir (PNG)
                  </button>
                  <Link
                    to={`/mockup-studio?designUrl=${encodeURIComponent(variation.imageUrl)}${draftId ? `&draftId=${draftId}` : ""}`}
                    className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors border border-emerald-200"
                  >
                    <Image className="h-3.5 w-3.5" />
                    Mockup'a Gönder
                  </Link>
                  <button
                    onClick={() => onSaveToDesigns(variation.imageUrl!, variation.category || `variation-${index}`)}
                    className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors border border-violet-200"
                  >
                    <Save className="h-3.5 w-3.5" />
                    Tasarımlara Ekle
                  </button>
                </>
              )}
              <button
                onClick={() => onCopy(variation.id, variation.result)}
                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 transition-colors"
              >
                {copiedId === variation.id ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                    <span className="text-emerald-600">Kopyalandı!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Prompt'u Kopyala
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {variation.status === "failed" && (
          <p className="text-sm text-red-600 py-2">{variation.result}</p>
        )}
      </div>
    </div>
  );
}
